<template>
  <v-sheet color="snow" rounded="xl" class="pa-4">
    <v-container :fluid="true">
      <v-container class="pa-0">
        <v-row>
          <v-col>
            <div v-if="props.title" class="text-h4 text-primary">{{ props.title }}</div>
            <div v-if="errorMsg" class="text-error">{{ errorMsg }}</div>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-model="toFaxNumber"
              label="To Fax Number"
              variant="outlined"
              bg-color="white"
              :rules="[requiredRule, phoneNumberRule]" />
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="templateItems.length > 0">
            <v-combobox
              variant="solo"
              :items="templateItems"
              label="Select a fax template"
              :model-value="selectedTemplate"
              @update:model-value="onTemplateSelected" />
          </v-col>
        </v-row>
        <v-row v-if="props.attachmentOptions.length > 0">
          <v-col>
            <v-combobox
              variant="outlined"
              bg-color="white"
              :items="props.attachmentOptions"
              label="Select an attachment"
              :model-value="selectedAttachment"
              clearable
              @update:model-value="onAttachmentSelected" />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-textarea v-model="content" label="Content" />
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="props.cancelButton" cols="4">
            <v-btn-lh :loading="loading" @click="onCancel">Cancel</v-btn-lh>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto" align="right">
            <v-btn-lh v-if="downloadLinks.length > 0" :disabled="true" :loading="loading">Attached</v-btn-lh>
            <template v-else>
              <v-btn-lh v-if="!showFileUpload" :loading="loading" @click="onAttachFile">Attach a File</v-btn-lh>
              <UploadFileComponent v-if="showFileUpload" @download-link="onDownloadLink" />
            </template>
          </v-col>
          <v-col cols="auto" align="right">
            <v-btn-lh
              :disabled="(!content && !selectedAttachment) || !toFaxNumber"
              :loading="loading"
              @click="onPreview">
              Preview
            </v-btn-lh>
          </v-col>
          <v-col cols="auto" align="right">
            <v-btn-lh :disabled="(!content && !selectedAttachment) || !toFaxNumber" :loading="loading" @click="onSend">
              {{ props.sendButtonText }}
            </v-btn-lh>
          </v-col>
        </v-row>
        <v-dialog v-model="showPreview" width="90%" height="90%">
          <v-card>
            <v-card-title>Preview</v-card-title>
            <v-card-subtitle> Send to: {{ toFaxNumber }} </v-card-subtitle>
            <v-card-text>
              <v-alert
                v-if="selectedAttachment?.value?.type === 'flattened_form'"
                type="info"
                variant="tonal"
                prominent
                class="mb-4">
                <template #prepend>
                  <v-icon size="large">mdi-file-document-outline</v-icon>
                </template>
                <div class="text-h6 font-weight-bold mb-1">Form Attachment</div>
                <div class="text-body-1">Filled prior authorization form will be attached to the fax.</div>
              </v-alert>
              <iframe :srcdoc="previewContent" width="100%" height="100%" frameborder="0"></iframe>
            </v-card-text>
            <v-card-actions>
              <v-btn-lh @click="showPreview = false">Close</v-btn-lh>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-container>
    </v-container>
  </v-sheet>
</template>

<script setup>
import { onMounted, ref, watch } from "vue";
import { templateService } from "@/services/templateService";
import { eFaxService } from "@/services/eFaxService";
import { IntakeOrder, IntakeDocument } from "@/models/intake";
import { ClientTemplate } from "@/models/templates";
import { requiredRule } from "@/utils/form";
import { isPhoneNumberValid } from "@/utils/validators";
import UploadFileComponent from "@/components/UploadFileComponent.vue";

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  types: {
    type: Array,
    required: false,
    default: () => [],
  },
  toFaxNumber: {
    type: String,
    required: false,
    default: null,
  },
  intakeOrder: {
    type: IntakeOrder,
    required: false,
    default: null,
  },
  intakeDocument: {
    type: IntakeDocument,
    required: false,
    default: null,
  },
  orderInfo: {
    type: Object,
    required: false,
    default: null,
  },
  cancelButton: {
    type: Boolean,
    required: false,
    default: true,
  },
  sendButtonText: {
    type: String,
    required: false,
    default: "Send Fax",
  },
  attachmentOptions: {
    type: Array,
    required: false,
    default: () => [],
  },
  preselectedAttachment: {
    type: Object,
    required: false,
    default: null,
  },
});
const emit = defineEmits(["onComplete", "onPreSend"]);

const templates = ref([]);
const templateItems = ref([]);
const showFileUpload = ref(false);
const selectedTemplate = ref(null);
const content = ref("");
const errorMsg = ref("");
const loading = ref(false);
const toFaxNumber = ref(props.toFaxNumber ? props.toFaxNumber : "");
const downloadLinks = ref([]);

const phoneNumberRule = (v) => {
  if (!v) return true; // requiredRule handles empty values
  return isPhoneNumberValid(v) ? true : "Please enter a valid fax number (10 digits)";
};
const showPreview = ref(false);
const previewContent = ref("");

onMounted(() => {
  fetchTemplates();
});

watch(
  () => props.toFaxNumber,
  (newValue) => {
    if (newValue !== undefined && newValue !== null) {
      toFaxNumber.value = newValue;
    }
  },
  { immediate: true }
);

watch(
  () => props.preselectedAttachment,
  (newValue) => {
    if (newValue) {
      selectedAttachment.value = newValue;
    }
  },
  { immediate: true }
);

const fetchTemplates = async () => {
  try {
    loading.value = true;
    errorMsg.value = "";
    const response = await templateService.getTemplates({
      templateTypes: props.types,
      intakeOrderId: props.intakeOrder?.id,
      orderInfoKey: props.orderInfo?.orderKey,
      intakeDocumentKey: props.intakeDocument?.documentKey,
    });
    templates.value = response.results.map((t) => new ClientTemplate(t));
    templateItems.value = templates.value.map((t) => ({
      title: t.name ?? t.templateType,
      value: t.id,
    }));
    if (templateItems.value.length > 0) {
      selectedTemplate.value = templateItems.value[0];
      onTemplateSelected(selectedTemplate.value);
    }
  } catch (error) {
    errorMsg.value = `Error fetching templates: ${error.message}`;
  } finally {
    loading.value = false;
  }
};

const onAttachFile = () => {
  showFileUpload.value = true;
};

const onDownloadLink = (download) => {
  downloadLinks.value.push(download.link);
};

const onTemplateSelected = (selected) => {
  selectedTemplate.value = selected;
  const template = templates.value.find((t) => t.id === selected.value);
  content.value = template?.editableContent ?? "";
};

const selectedAttachment = ref(props.preselectedAttachment || null);

const onAttachmentSelected = (selected) => {
  // Allow clearing selection (null or undefined)
  selectedAttachment.value = selected || null;
};

const resetForm = () => {
  content.value = "";
  selectedAttachment.value = null;
  downloadLinks.value = [];
  showPreview.value = false;
  previewContent.value = "";
  errorMsg.value = "";
};

const onSend = async () => {
  try {
    errorMsg.value = "";
    loading.value = true;

    // Build attachment data if selected
    const attachmentData = selectedAttachment.value?.value
      ? {
          type: selectedAttachment.value.value.type,
          value: selectedAttachment.value.value.type === "response_fax" ? selectedAttachment.value.value.s3_url : null,
        }
      : null;
    const isFlattenedFormAttachment = attachmentData?.type === "flattened_form";

    if (isFlattenedFormAttachment) {
      try {
        await new Promise((resolve, reject) => {
          emit("onPreSend", resolve, reject);
        });
      } catch (saveError) {
        errorMsg.value = "Failed to save prior authorization form before faxing. Please try again.";
        loading.value = false;
        return;
      }
    }

    const requestData = {
      text: content.value,
      toFaxNumber: toFaxNumber.value,
      fileLinks: downloadLinks.value,
    };
    if (selectedTemplate.value?.value) {
      requestData.templateId = selectedTemplate.value.value;
    }
    if (attachmentData) {
      requestData.attachment = attachmentData;
    }
    if (props.intakeOrder) {
      requestData.intakeOrderId = props.intakeOrder.id;
    }
    if (props.orderInfo) {
      requestData.orderInfoKey = props.orderInfo.orderKey;
    }
    if (props.intakeDocument) {
      requestData.intakeDocumentKey = props.intakeDocument.documentKey;
    }
    const response = await eFaxService.postSendOutgoingFax(requestData);
    emit("onComplete", { sent: response });
    // Clear form after successful send
    resetForm();
  } catch (error) {
    if (error instanceof Error && error.format) {
      errorMsg.value = error.format();
    } else {
      errorMsg.value = `Error sending fax: ${error.message || "Unknown error"}`;
    }
  } finally {
    loading.value = false;
  }
};

const onCancel = () => {
  emit("onComplete", { sent: null });
};

const onPreview = async () => {
  showPreview.value = true;
  loading.value = true;
  try {
    const attachmentData = selectedAttachment.value?.value
      ? {
          type: selectedAttachment.value.value.type,
          value: selectedAttachment.value.value.type === "response_fax" ? selectedAttachment.value.value.s3_url : null,
        }
      : null;
    const isFlattenedFormAttachment = attachmentData?.type === "flattened_form";
    const shouldPreviewAttachment = attachmentData && !isFlattenedFormAttachment;

    const baseRequestData = {
      content: content.value || "",
    };
    if (props.orderInfo?.orderKey) {
      baseRequestData.orderInfoKey = props.orderInfo.orderKey;
    }
    if (props.intakeOrder?.id) {
      baseRequestData.intakeOrderId = props.intakeOrder.id;
    }
    if (props.intakeDocument?.documentKey) {
      baseRequestData.intakeDocumentKey = props.intakeDocument.documentKey;
    }

    let response = "";

    if (selectedTemplate.value) {
      if (shouldPreviewAttachment) {
        const requestPayload = {
          ...baseRequestData,
          attachment: attachmentData,
          template_id: selectedTemplate.value.value,
        };
        response = await templateService.getFaxContent(requestPayload);
      } else {
        response = await templateService.getTemplatePreview(selectedTemplate.value.value, content.value || "");
      }
    } else if (shouldPreviewAttachment) {
      const requestPayload = { ...baseRequestData, attachment: attachmentData };
      response = await templateService.getFaxContent(requestPayload);
    } else if (baseRequestData.content) {
      response = await templateService.getFaxContent(baseRequestData);
    }

    if (!response) {
      response = "<p style='font-family: DM Sans; color: #5E7898;'>No additional content selected for preview.</p>";
    }

    previewContent.value = response;
  } catch (error) {
    errorMsg.value = `Error fetching preview: ${error.message}`;
  } finally {
    loading.value = false;
  }
};
</script>

<style lang="scss" scoped></style>
